import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dryingTechnologyOptions } from "@/lib/dryerTechnology";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useApplianceDryingTechnology(applianceId?: string | null) {
  const enabled = !!applianceId && UUID.test(applianceId);
  return useQuery({
    queryKey: ["appliance_drying_technology", applianceId],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appliances")
        .select("category, appliance_class, dryer_technology")
        .eq("id", applianceId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function DryingTechnologyField({ applianceId, hideWarning = false }: { applianceId?: string; hideWarning?: boolean }) {
  const { t } = useTranslation("appliance");
  const qc = useQueryClient();
  const { data } = useApplianceDryingTechnology(applianceId);
  if (!data) return null;
  const options = dryingTechnologyOptions(data.category, data.appliance_class);
  if (options.length === 0) return null;
  const current = data.dryer_technology && options.includes(data.dryer_technology as never)
    ? data.dryer_technology
    : undefined;

  const save = async (value: string) => {
    const { error } = await supabase.from("appliances").update({ dryer_technology: value }).eq("id", applianceId!);
    if (error) {
      toast.error(t("dryingTechnology.saveFailed"));
      return;
    }
    qc.setQueryData(["appliance_drying_technology", applianceId], { ...data, dryer_technology: value });
    qc.invalidateQueries({ queryKey: ["harvest_recommendations"] });
    toast.success(t("dryingTechnology.saved"));
  };

  return (
    <div className="sm:col-span-2">
      <dt className="text-xs text-muted-foreground">{t("dryingTechnology.label")}</dt>
      <dd className="flex flex-wrap items-center gap-2 mt-0.5">
        <Select value={current} onValueChange={save}>
          <SelectTrigger className="h-8 w-48">
            <SelectValue placeholder={t("dryingTechnology.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o} value={o}>{t(`dryingTechnology.options.${o}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!current && !hideWarning && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700" role="status">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="font-medium">{t("dryingTechnology.unknownTitle")}</span>
            <span className="text-muted-foreground">{t("dryingTechnology.unknownHint")}</span>
          </span>
        )}
      </dd>
    </div>
  );
}
